/**
 * @author Mathieu Chantot <mathieu.chantot@hes-so.ch>
 * @date 02/24
 */

import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import Circle from "mapbox-gl-circle";
import { sector, coordAll } from "@turf/turf";

import PaintMode from "@thirdparty/draw/src/index.js";

import markerImage from "@public/img/navigation-marker-grey.png";

const turf = { sector, coordAll };

export default function Map({ map, draw, vehicle, mapMovement, maxAngle, circleRadius }) {
  const mapContainer = useRef(null);
  const marker = useRef(null);
  const circle = useRef(null);
  const sector1 = useRef(null);
  const sector2 = useRef(null);
  const [zoom, setZoom] = useState(20);

  /**
   * @brief Initialize the map and the draw tool
   */
  // Called at initialization.
  useEffect(() => {
    // Initialize map
    if (map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style:
        "https://vectortiles.geo.admin.ch/styles/ch.swisstopo.leichte-basiskarte-imagery.vt/style.json",
      center: [vehicle.localization.longitude, vehicle.localization.latitude],
      zoom: zoom,
    });
    // Initialize draw tool
    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      modes: Object.assign(
        {
          draw_paint_mode: PaintMode,
          static_mode: StaticMode,
        },
        MapboxDraw.modes
      ),
      defaultMode: "static_mode",
    });
    map.current.addControl(draw.current);
  });

  // Called when the map is initialized or when the vehicle position is updated.
  useEffect(() => {
    // Create features
    // Polygon
    const center = [vehicle.localization.longitude, vehicle.localization.latitude];
    const radius = 0.003; // radius in kilometers
    const bearing1 = vehicle.localization.heading - maxAngle; // start bearing in degrees
    const bearing2 = vehicle.localization.heading + maxAngle; // end bearing in degrees
    const options = { steps: 50, units: "kilometers", properties: { fill: "#0f0" } };
    sector1.current = turf.sector(center, radius, bearing1, bearing2, options);
    sector2.current = turf.sector(
      center,
      radius,
      (bearing1 + 180) % 360,
      (bearing2 + 180) % 360,
      options
    );

    // Circle
    if (circle.current) {
      circle.current.setCenter({
        lat: vehicle.localization.latitude,
        lng: vehicle.localization.longitude,
      });
    } else {
      circle.current = new Circle(
        { lat: vehicle.localization.latitude, lng: vehicle.localization.longitude },
        circleRadius,
        {
          minRadius: 2,
          fillColor: "lightblue",
        }
      );
      console.log("Circle fillColor:", circle.current.options.fillColor);
    }

    // Marker
    if (!marker.current) {
      //Marker
      const img = document.createElement("img");
      img.className = "marker";
      img.height = Math.pow(2, map.current.getZoom()) / 10000;
      img.src = markerImage;
      marker.current = new mapboxgl.Marker({ element: img });
    } else {
      marker.current.setRotationAlignment("map");
      marker.current.setRotation(vehicle.localization.heading);
      marker.current.setLngLat([vehicle.localization.longitude, vehicle.localization.latitude]);
    }
    // Add features to the map
    map.current.on("load", () => {
      // Polygon
      map.current.addSource("insideSectorSource1ID", {
        type: "geojson",
        data: sector1.current,
      });
      map.current.addLayer({
        id: "insideSectorLayer1ID",
        type: "fill",
        source: "insideSectorSource1ID",
        layout: {},
        paint: {
          "fill-color": "#fff", // blue color fill
          "fill-opacity": 0,
        },
      });
      map.current.addSource("insideSectorSource2ID", {
        type: "geojson",
        data: sector2.current,
      });
      map.current.addLayer({
        id: "insideSectorLayer2ID",
        type: "fill",
        source: "insideSectorSource2ID",
        layout: {},
        paint: {
          "fill-color": "#fff", // blue color fill
          "fill-opacity": 0,
        },
      });
      // Circle
      circle.current.addTo(map.current);
      // Marker
      console.log("marker", marker.current);
      marker.current.addTo(map.current);
    });
    // Update Features
    // Polygon
    if (sector1.current && map.current.getSource("insideSectorSource1ID")) {
      map.current.getSource("insideSectorSource1ID").setData(sector1.current);
    }
    if (sector2.current && map.current.getSource("insideSectorSource2ID")) {
      map.current.getSource("insideSectorSource2ID").setData(sector2.current);
    }
    map.current.on("zoomend", () => {
      const img = marker.current.getElement();
      img.height = Math.pow(2, map.current.getZoom()) / 10000;
      console.log("zoom", Math.pow(2, map.current.getZoom()) / 10000);
    });
  }, [vehicle]);

  useEffect(() => {
    if (map.current) {
      if (mapMovement) {
        enableMovement();
      } else {
        disableMovement();
      }
    }
  }, [mapMovement]);

  /**
   * @brief Enable map movement.
   */
  function enableMovement() {
    map.current["dragPan"].enable();
    map.current["scrollZoom"].enable();
    map.current["boxZoom"].enable();
    map.current["dragRotate"].enable();
    map.current["keyboard"].enable();
    map.current["doubleClickZoom"].enable();
    map.current["touchZoomRotate"].enable();
  }

  /**
   * @brief Disable map movement.
   */
  function disableMovement() {
    map.current["dragPan"].disable();
    map.current["scrollZoom"].disable();
    map.current["boxZoom"].disable();
    map.current["dragRotate"].disable();
    map.current["keyboard"].disable();
    map.current["doubleClickZoom"].disable();
    map.current["touchZoomRotate"].disable();
  }
  return (
    <div
      ref={mapContainer}
      id="map"
      className="map-container"
      style={{ height: "100%", width: "100%" }}
    />
  );
}
